import { useEffect, useRef } from "react";
import moment from "moment";
import {
  isBooleanFieldChecked,
  toBooleanFieldValue,
} from "utils/helpers/booleanField";
import { DATE_FORMAT } from "utils/constants";
import { readItemField } from "utils/validation-rules";

const INPUT_KIND_RADIO_GROUP = "radioGroup";
const INPUT_TYPE_FILE = "file";
const INPUT_TYPE_DATE = "date";
const INPUT_TYPE_CHECKBOX = "checkbox";
const TAG_INPUT = "INPUT";
const TAG_TEXTAREA = "TEXTAREA";
const TAG_SELECT = "SELECT";
const ACTION_REMOVE_ITEM = "removeItem";
const SELECTED_TRUE = "true";
const SELECTED_FALSE = "false";
const EMPTY_VALUE = "";
const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const formatDisplayValue = (val) => {
  if (val instanceof Date) {
    const parsed = moment(val);
    return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
  }
  if (typeof val !== "string" || !ISO_DATE_PATTERN.test(val.trim())) {
    return String(val);
  }
  const parsed = moment(val);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
};

const toSelectedFlag = (isSelected) =>
  isSelected ? SELECTED_TRUE : SELECTED_FALSE;

const findFieldDef = (registryFields, fieldName) =>
  (registryFields || []).find((f) => f.key === fieldName) || null;

const replaceSelectOptions = (container, registryFields) => {
  const selects = container.querySelectorAll("select[data-field]");
  for (const el of selects) {
    const path = el.getAttribute("data-field");
    const fieldName = path.split(".").slice(1).join(".");
    const fieldDef = findFieldDef(registryFields, fieldName);
    if (!fieldDef || !Array.isArray(fieldDef.options)) continue;
    for (const o of Array.from(el.options)) {
      if (!o.disabled && o.value !== EMPTY_VALUE) o.remove();
    }
    for (const opt of fieldDef.options) {
      const optEl = el.ownerDocument.createElement("option");
      optEl.value = opt.value;
      optEl.textContent = opt.label;
      el.appendChild(optEl);
    }
  }
};

const populateValues = (container, item) => {
  const fieldEls = container.querySelectorAll("[data-field]");
  for (const el of fieldEls) {
    const path = el.getAttribute("data-field");
    const fieldName = path.split(".").slice(1).join(".");
    const val = readItemField(item, fieldName);
    if (val === undefined || val === null) continue;
    const tag = el.tagName;
    const radioButtons = el.querySelectorAll("[data-value]");
    if (
      el.getAttribute("data-input-type") === INPUT_KIND_RADIO_GROUP ||
      radioButtons.length
    ) {
      for (const sib of radioButtons) {
        sib.setAttribute(
          "data-selected",
          toSelectedFlag(sib.getAttribute("data-value") === String(val)),
        );
      }
      continue;
    }
    if (tag === TAG_INPUT || tag === TAG_TEXTAREA || tag === TAG_SELECT) {
      if (el.type === INPUT_TYPE_FILE) continue;
      if (el.type === INPUT_TYPE_DATE) {
        el.value = moment(val).format("YYYY-MM-DD");
      } else if (el.type === INPUT_TYPE_CHECKBOX) {
        el.checked = isBooleanFieldChecked(val);
      } else {
        el.value = String(val);
      }
    } else {
      el.textContent = formatDisplayValue(val);
      const pillWhen = el.getAttribute("data-pill-when");
      if (pillWhen) {
        const isPill = String(val).trim() === pillWhen;
        el.classList.toggle("ge-pill", isPill);
        el.classList.toggle("ge-amount", !isPill);
      }
    }
  }
};

export const CollectionItem = ({
  template,
  item,
  onFieldChange,
  onRemove,
  onAction,
  registryFields,
  selected,
}) => {
  const containerRef = useRef(null);
  const itemRef = useRef(item);
  const onFieldChangeRef = useRef(onFieldChange);
  const onRemoveRef = useRef(onRemove);
  const onActionRef = useRef(onAction);
  const registryFieldsRef = useRef(registryFields);

  itemRef.current = item;
  onFieldChangeRef.current = onFieldChange;
  onRemoveRef.current = onRemove;
  onActionRef.current = onAction;
  registryFieldsRef.current = registryFields;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.innerHTML = template;
    container.removeAttribute("data-kayros-slot");
    const itemRoot = container.firstElementChild;
    if (itemRoot) {
      itemRoot.hidden = false;
      itemRoot.removeAttribute("hidden");
    }
    const innerSlots = container.querySelectorAll("[data-kayros-slot]");
    for (const ds of innerSlots) ds.removeAttribute("data-kayros-slot");

    replaceSelectOptions(container, registryFieldsRef.current);
    populateValues(container, itemRef.current || {});

    const handleInput = (e) => {
      const target = e.target;
      if (!target || !target.dataset || !target.dataset.field) return;
      if (target.type === INPUT_TYPE_CHECKBOX) return;
      e.stopPropagation();
      const fieldName = target.dataset.field.split(".").slice(1).join(".");
      onFieldChangeRef.current(fieldName, target.value);
    };

    const handleChange = (e) => {
      const target = e.target;
      if (!target || !target.dataset || !target.dataset.field) return;
      if (target.type === INPUT_TYPE_CHECKBOX) {
        e.stopPropagation();
        const fieldName = target.dataset.field.split(".").slice(1).join(".");
        onFieldChangeRef.current(
          fieldName,
          toBooleanFieldValue(target.checked),
        );
        return;
      }
      if (target.tagName === TAG_SELECT) {
        e.stopPropagation();
        const fieldName = target.dataset.field.split(".").slice(1).join(".");
        onFieldChangeRef.current(fieldName, target.value);
      }
    };

    const handleClick = (e) => {
      const actionBtn = e.target.closest && e.target.closest("[data-action]");
      if (actionBtn) {
        const action = actionBtn.getAttribute("data-action");
        if (action === ACTION_REMOVE_ITEM) {
          e.preventDefault();
          e.stopPropagation();
          onRemoveRef.current && onRemoveRef.current();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (onActionRef.current) onActionRef.current(action, itemRef.current);
        return;
      }
      const valueBtn = e.target.closest && e.target.closest("[data-value]");
      if (valueBtn) {
        const fieldEl = valueBtn.closest("[data-field]");
        if (!fieldEl) return;
        e.preventDefault();
        e.stopPropagation();
        const value = valueBtn.getAttribute("data-value");
        const fieldName = fieldEl
          .getAttribute("data-field")
          .split(".")
          .slice(1)
          .join(".");
        onFieldChangeRef.current(fieldName, value);
        const siblings = fieldEl.querySelectorAll("[data-value]");
        for (const sib of siblings) {
          sib.setAttribute(
            "data-selected",
            toSelectedFlag(sib.getAttribute("data-value") === value),
          );
        }
      }
    };

    container.addEventListener("input", handleInput);
    container.addEventListener("change", handleChange);
    container.addEventListener("click", handleClick);

    return () => {
      container.removeEventListener("input", handleInput);
      container.removeEventListener("change", handleChange);
      container.removeEventListener("click", handleClick);
    };
  }, [template]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    replaceSelectOptions(container, registryFields);
    populateValues(container, itemRef.current || {});
  }, [registryFields]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    populateValues(container, item || {});
  }, [item]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const itemEl = container.firstElementChild;
    if (!itemEl) return;
    itemEl.setAttribute("data-selected", toSelectedFlag(selected));
  }, [selected, template]);

  return <div ref={containerRef} />;
};

export default CollectionItem;
